import { Info, Activity } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import { StatusCard } from './status-card';
import { ModeCard } from './mode-card';
import { Mode, Status } from '@/types';
import { formatNumber } from '@/utils/formatNumber';
import { IconTitle } from '@/components/ui/icon-title';

interface Unit {
  status: Status;
  type: Mode;
  count: number;
}

interface OperationCardProps {
  units?: Unit[];
  info: {
    averageWorking: number
    avgToday: number
  };
  variant?: 'autopilot' | 'overview';
}

export function OperationCard({ units, info, variant = 'autopilot' }: OperationCardProps) {
  const isOverview = variant === 'overview';
  
  return (
    <div className={`bg-white rounded-[10px] p-2 border border-light-grey gap-1.5 flex flex-col ${isOverview ? 'h-[180px]' : 'shadow-card'}`}>
      {isOverview ? (
        <>
          {/* Overview Layout */}
          <IconTitle icon={Activity} title="Operation" />

          <div className="flex justify-between items-start gap-3">
            <div className="flex flex-col gap-[1px]">
              <div className="flex items-center gap-1">
                <p className="paragraph-10 text-dark-grey">Avg. Working</p>
                <div
                  data-tooltip-id="operation-tooltip"
                  data-tooltip-place="right"
                  className="cursor-pointer text-gray-400 hover:text-gray-600 flex"
                >
                  <Info className="inline-block h-[10px] w-[10px] ml-1 text-primary" />
                </div>
                <Tooltip arrowColor='bg-white' id="operation-tooltip" opacity={1} style={{
                  backgroundColor: 'white',
                  padding: '4px 6px',
                  gap: '8px',
                  width: "165px",
                  boxShadow: "-4px -4px 20px 0px #7090B03D",
                  backdropFilter: "blur(20px)",
                }}>
                  <p className='paragraph-9 text-dark-blue'>Working % indicates the AHU's operational level relative to its maximum capacity. Values are averaged across all active units.</p>
                </Tooltip>
              </div>
              <div className="flex items-baseline gap-0">
                <span className="heading-6">{formatNumber(info.averageWorking)}</span>
                <span className="paragraph-16">%</span>
              </div>
            </div>

            <div className="flex flex-col gap-[1px] text-right">
              <p className='paragraph-9 text-dark-grey'>Avg. Set Temp</p>
              <p className='paragraph-9 text-dark-grey'>{formatNumber(info.avgToday)}°C</p>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Autopilot Layout */}
          <div className="flex gap-1.5">
            <div className='flex flex-col gap-1.5'>
              <div className="flex items-center gap-1.5">
                <div className="h-[18px] w-[18px] bg-light-grey rounded flex items-center justify-center">
                  <Activity className="h-3 w-3 text-primary" />
                </div>
                <h3 className="text-dark-blue heading-7">Operation</h3>
              </div>
              <div className="flex flex-col gap-[1px]">
                <div className="flex items-center gap-1">
                  <p className="paragraph-11 text-dark">Working %</p>
                  <div
                    data-tooltip-id="my-tooltip"
                    data-tooltip-place="right"
                    className="cursor-pointer text-gray-400 hover:text-gray-600 flex"
                  >
                    <Info className="inline-block h-[10px] w-[10px] ml-1 text-primary" />
                  </div>
                  <Tooltip arrowColor='bg-white' id="my-tooltip" opacity={1} style={{
                    backgroundColor: 'white',
                    padding: '4px 6px',
                    gap: '8px',
                    width: "165px",
                    boxShadow: "-4px -4px 20px 0px #7090B03D",
                    backdropFilter: "blur(20px)",
                  }}>
                    <p className='paragraph-9 text-dark-blue'>Working % indicates the AHU's operational level relative to its maximum capacity. Values are averaged across all active units.</p>
                  </Tooltip>
                </div>
                <div className="flex items-baseline gap-0">
                  <span className="heading-6">{formatNumber(info.averageWorking)}</span>
                  <span className="paragraph-16">%</span>
                </div>
                <div className="flex flex-col gap-0">
                  <p className='paragraph-9 text-dark-grey'>Avg. Set Temp</p>
                  <p className='paragraph-9 text-dark-grey'>{formatNumber(info.avgToday)}°C</p>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-1 grow h-fit'>
              <div className="flex-1 flex gap-1 h-fit">
                <StatusCard
                  type="alarm"
                  count={units?.find(v => v.status == "alarm")?.count || 0}
                  bgColor="bg-danger/20"
                  dotColor="bg-dark-orange"
                  textColor="text-danger"
                />
                <StatusCard
                  type="on"
                  count={units?.find(v => v.status == "on")?.count || 0}
                  bgColor="bg-[#DCEDDD]"
                  dotColor="bg-[#15B8B4]"
                  textColor="text-dark-green"
                />
                <StatusCard
                  type="off"
                  count={units?.find(v => v.status == "off")?.count || 0}
                  bgColor="bg-gray-200/40"
                  dotColor="bg-[#B4B4B4]"
                  textColor="text-dark-grey"
                />
              </div>
              <div className="flex gap-[4px] min-h-[2.75rem]">
                <ModeCard type="cool" count={units?.find(v => v.type == "cool")?.count || 0} />
                <ModeCard type="fan" count={units?.find(v => v.type == "fan")?.count || 0} />
                <ModeCard type="dry" count={units?.find(v => v.type == "dry")?.count || 0} />
              </div>
            </div>
          </div>
        </>
      )}

      {isOverview && (
        <>
          <div className="flex gap-1 mt-auto">
            <StatusCard
              type="alarm"
              count={units?.find(v => v.status == "alarm")?.count || 0}
              bgColor="bg-danger/20"
              dotColor="bg-dark-orange"
              textColor="text-danger"
            />
            <StatusCard
              type="on"
              count={units?.find(v => v.status == "on")?.count || 0}
              bgColor="bg-[#DCEDDD]"
              dotColor="bg-[#15B8B4]"
              textColor="text-dark-green"
            />
            <StatusCard
              type="off"
              count={units?.find(v => v.status == "off")?.count || 0}
              bgColor="bg-gray-200/40"
              dotColor="bg-[#B4B4B4]"
              textColor="text-dark-grey"
            />
          </div>

          <div className="flex gap-[4px] min-h-[2.75rem]">
            <ModeCard type="cool" count={units?.find(v => v.type == "cool")?.count || 0} />
            <ModeCard type="fan" count={units?.find(v => v.type == "fan")?.count || 0} />
            <ModeCard type="dry" count={units?.find(v => v.type == "dry")?.count || 0} />
          </div>
        </>
      )}
    </div>
  );
}